import { useState, useContext, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Play, Pause, Volume2, Lock, Check, ChevronDown, ChevronUp, Compass } from "lucide-react";
import { EnlightenmentContext } from "@/context/EnlightenmentContext";
import GarmentCollection from "@/components/GarmentCollection";
import RoadAheadCard from "@/components/RoadAheadCard";
import {
  fadeInUp, fadeInUpDelayed, fadeInLeft, fadeIn, expandHeight,
  tapHover, tapHoverIcon, pulseRing,
} from "@/lib/motionVariants";

const Journey = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    currentTier,
    blueberries,
    carrots,
    cycleCount,
    meditationComplete,
    completeMeditation,
    getCurrentTierData,
    allTiers,
    showTierCelebration,
    clearTierCelebration,
    animalSpirit,
    angelTitle,
    angelEmoji,
    sessionLoaded,
    dailyOracle
  } = useContext(EnlightenmentContext);

  const tierData = getCurrentTierData();
  const nextTierData = currentTier + 1 < allTiers.length ? allTiers[currentTier + 1] : null;
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioProgress, setAudioProgress] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [showRoadAheadModal, setShowRoadAheadModal] = useState(false);
  const [meditating, setMeditating] = useState(false);
  const [meditationTimer, setMeditationTimer] = useState(0);
  const audioRef = useRef(null);
  const meditationInterval = useRef(null);
  const tierMapScrollRef = useRef(null);
  const currentTierNodeRef = useRef(null);

  // Tier 36 (Yakav-Es-Rayl) grants the seeker their Holy Guardian Angel title.
  // Once `angelTitle` is set, the journey-map marker switches from the default
  // angel-face (Tier 27+) to the Function-specific emoji (e.g. 🕊️ Angel of Peace).
  // Tier 27+ otherwise falls back to 😇; pre-27 falls back to the animal spirit or seed.
  const hasAngelTitle = !!angelTitle;
  const userMapMarker = hasAngelTitle
    ? (angelEmoji || '👼')
    : currentTier >= 27
      ? '😇'
      : (animalSpirit?.emoji || '🌱');
  const userMarkerLabel = hasAngelTitle
    ? angelTitle
    : currentTier >= 27
      ? 'You'
      : (animalSpirit ? animalSpirit.name : 'You');

  const MEDITATION_DURATION = 60;

  useEffect(() => {
    return () => {
      if (meditationInterval.current) clearInterval(meditationInterval.current);
    };
  }, []);

  // Auto-open the Road Ahead modal when arriving via ?preview=road-ahead (from Home shortcut)
  useEffect(() => {
    if (searchParams.get('preview') === 'road-ahead' && nextTierData) {
      setShowRoadAheadModal(true);
      // Clear the param so refreshing doesn't keep re-opening
      const next = new URLSearchParams(searchParams);
      next.delete('preview');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, nextTierData, setSearchParams]);

  // Auto-scroll the path map to the user's current tier node on mount (and when tier changes)
  // — so the user always sees where they ARE, not the start of the map.
  useEffect(() => {
    const node = currentTierNodeRef.current;
    const container = tierMapScrollRef.current;
    if (!node || !container || !sessionLoaded) return;
    const t = setTimeout(() => {
      try {
        const nodeLeft = node.offsetLeft;
        const nodeWidth = node.offsetWidth;
        const containerWidth = container.clientWidth;
        // Centre the node horizontally
        container.scrollTo({
          left: Math.max(0, nodeLeft - containerWidth / 2 + nodeWidth / 2),
          behavior: 'smooth'
        });
      } catch (err) { console.debug('[Journey] auto-scroll failed:', err?.message); }
    }, 250); // give layout a tick to settle
    return () => clearTimeout(t);
  }, [currentTier, sessionLoaded]);

  const toggleAudio = () => {
    if (!audioRef.current) return;
    if (isPlaying) { audioRef.current.pause(); }
    else { audioRef.current.play(); }
    setIsPlaying(!isPlaying);
  };

  const handleTimeUpdate = () => {
    if (!audioRef.current) return;
    setAudioProgress(audioRef.current.currentTime);
    setAudioDuration(audioRef.current.duration || 0);
  };

  const handleAudioEnd = () => { setIsPlaying(false); setAudioProgress(0); };

  const startMeditation = () => {
    setMeditating(true);
    setMeditationTimer(0);
    meditationInterval.current = setInterval(() => {
      setMeditationTimer(prev => {
        if (prev >= MEDITATION_DURATION - 1) {
          clearInterval(meditationInterval.current);
          setMeditating(false);
          completeMeditation();
          return MEDITATION_DURATION;
        }
        return prev + 1;
      });
    }, 1000);
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen px-4 py-8 relative">
      {/* Loading State */}
      {!sessionLoaded && (
        <div className="flex items-center justify-center min-h-[60vh]">
          <motion.div
            animate={pulseRing.animate}
            transition={pulseRing.transition}
            className="text-center"
          >
            <div className="w-16 h-16 mx-auto mb-4 rounded-full overflow-hidden" style={{ boxShadow: '0 8px 24px rgba(79,55,139,0.2)' }}>
              <img src="https://customer-assets.emergentagent.com/job_scent-select-bunny/artifacts/kq5buvwj_3acdc1ca-655b-474d-9a20-97c4a3a5100a.png"
                alt="Papaya" className="w-full h-full object-cover" />
            </div>
            <p className="text-sm" style={{ color: '#8B7DA8', fontFamily: "'Nunito', sans-serif" }}>Loading your journey...</p>
          </motion.div>
        </div>
      )}

      {sessionLoaded && (
      <>
      {/* Tier Celebration Modal */}
      <AnimatePresence>
        {showTierCelebration && (
          <motion.div
            initial={fadeIn.initial}
            animate={fadeIn.animate}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm"
            onClick={clearTierCelebration}
            data-testid="tier-celebration-modal"
          >
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.5, opacity: 0 }}
              className="bg-white rounded-3xl p-10 max-w-md mx-4 text-center"
              style={{ boxShadow: '0 30px 80px rgba(79, 55, 139, 0.4)' }}
              onClick={e => e.stopPropagation()}
            >
              <div className="text-5xl mb-4">&#x1F955;</div>
              <h2 className="text-3xl font-bold mb-3" style={{ fontFamily: "'Fredoka', sans-serif", color: '#4F378B' }}>
                {showTierCelebration.completedTier} Complete
              </h2>
              <p className="text-base mb-2" style={{ fontFamily: "'Nunito', sans-serif", color: '#5C5470' }}>
                +1 Carrot earned
              </p>
              <p className="text-sm mb-6" style={{ color: '#8B7DA8' }}>
                You now enter the <strong>{showTierCelebration.newTier}</strong> tier.
              </p>
              <button
                onClick={clearTierCelebration}
                className="px-8 py-3 rounded-full text-white font-semibold"
                style={{ background: 'linear-gradient(135deg, #4F378B 0%, #7B5EA7 100%)' }}
                data-testid="celebration-dismiss"
              >
                Continue the Path
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="max-w-2xl mx-auto relative z-10">
        {/* Back */}
        <motion.button
          initial={fadeInLeft.initial}
          animate={fadeInLeft.animate}
          onClick={() => navigate('/')}
          className="flex items-center gap-2 mb-8 text-sm font-medium"
          style={{ color: '#5C5470', fontFamily: "'Nunito', sans-serif" }}
          data-testid="back-button"
        >
          <ArrowLeft size={18} />
          Back to Primordeia
        </motion.button>

        {/* Tier Header */}
        <motion.div {...fadeInUp} className="text-center mb-8">
          <h1
            className="text-4xl sm:text-5xl font-bold mb-2"
            style={{
              fontFamily: "'Fredoka', sans-serif",
              background: 'linear-gradient(135deg, #4F378B 0%, #7B5EA7 50%, #D4A574 100%)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text'
            }}
            data-testid="tier-title"
          >
            {tierData.name}
          </h1>
          <p className="text-base" style={{ color: '#5C5470', fontFamily: "'Nunito', sans-serif" }}>
            Tier {tierData.level} of {allTiers.length}
          </p>
        </motion.div>

        {/* Progress */}
        <motion.div
          {...fadeInUpDelayed(0.1)}
          className="rounded-2xl p-6 mb-6"
          style={{ backgroundColor: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(12px)', boxShadow: '0 8px 32px rgba(79,55,139,0.1)' }}
          data-testid="progress-section"
        >
          <div className="flex justify-between items-center mb-3">
            <span className="text-sm font-semibold" style={{ color: '#4F378B', fontFamily: "'Nunito', sans-serif" }}>
              Cycles Completed
            </span>
            <span className="text-sm font-bold" style={{ color: '#D4A574', fontFamily: "'Fredoka', sans-serif" }}>
              {blueberries} / 5 blueberries
            </span>
          </div>
          <div className="w-full h-3 rounded-full overflow-hidden" style={{ backgroundColor: '#E8E0F0' }}>
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${(blueberries / 5) * 100}%` }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="h-full rounded-full"
              style={{ background: 'linear-gradient(90deg, #4F378B 0%, #7B5EA7 50%, #D4A574 100%)' }}
            />
          </div>
          <p className="text-xs mt-3" style={{ color: '#8B7DA8' }}>
            Each cycle: 5 exchanges with Papaya. She picks your most insightful statement and awards a blueberry. 5 blueberries = 1 carrot = tier graduation.
          </p>
          <div className="flex justify-between mt-4">
            <div className="text-center">
              <div className="text-2xl font-bold" style={{ color: '#7B5EA7', fontFamily: "'Fredoka', sans-serif" }}>{blueberries}</div>
              <div className="text-xs" style={{ color: '#8B7DA8' }}>Blueberries</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold" style={{ color: '#D4A574', fontFamily: "'Fredoka', sans-serif" }}>{carrots}</div>
              <div className="text-xs" style={{ color: '#8B7DA8' }}>{currentTier >= 26 ? 'Crow Feathers' : 'Carrots'}</div>
            </div>
          </div>
        </motion.div>

        {/* Daily Oracle */}
        {dailyOracle && (
          <motion.div
            {...fadeInUpDelayed(0.12)}
            className="rounded-2xl p-5 mb-6 relative overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, #1A1030 0%, #2D1F50 60%, #1A2810 100%)',
              boxShadow: '0 8px 32px rgba(26, 16, 48, 0.25)'
            }}
            data-testid="journey-daily-oracle"
          >
            <div className="absolute inset-0 opacity-5"
              style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(212,165,116,0.5) 0%, transparent 70%)' }} />
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: '#D4A574', fontFamily: "'Nunito', sans-serif" }}>
                  Today's Oracle
                </span>
                <div className="flex-1 h-px" style={{ background: 'linear-gradient(90deg, #D4A574, transparent)' }} />
              </div>
              <p className="text-sm leading-relaxed" style={{ color: '#E8E0F8', fontFamily: "'Nunito', sans-serif" }}>
                {dailyOracle.message}
              </p>
            </div>
          </motion.div>
        )}

        {/* Spirit Animal Card */}
        {animalSpirit && (
          <motion.div
            {...fadeInUpDelayed(0.15)}
            className="rounded-2xl p-6 mb-6 relative overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, #1A1030 0%, #2D1F50 40%, #1A2810 100%)',
              boxShadow: '0 12px 40px rgba(26, 16, 48, 0.3)'
            }}
            data-testid="spirit-animal-card"
          >
            {/* Subtle shimmer overlay */}
            <div className="absolute inset-0 opacity-10"
              style={{
                background: 'radial-gradient(ellipse at 30% 20%, rgba(212,165,116,0.4) 0%, transparent 60%), radial-gradient(ellipse at 70% 80%, rgba(79,55,139,0.3) 0%, transparent 60%)'
              }} />
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs font-bold uppercase tracking-widest" style={{ color: '#D4A574', fontFamily: "'Nunito', sans-serif" }}>
                  Spirit Animal
                </span>
                <div className="flex-1 h-px" style={{ background: 'linear-gradient(90deg, #D4A574, transparent)' }} />
              </div>
              <div className="flex items-center gap-4">
                <div className="text-5xl">{animalSpirit.emoji}</div>
                <div>
                  <h3 className="text-2xl font-bold mb-1" style={{ fontFamily: "'Fredoka', sans-serif", color: '#F0E8F8' }}>
                    {animalSpirit.name}
                  </h3>
                  <p className="text-sm leading-relaxed" style={{ color: '#B0A5C0', fontFamily: "'Nunito', sans-serif" }}>
                    {animalSpirit.description || `Your primal sacral energy — the ${animalSpirit.name.toLowerCase()} is your oldest ally, the creature that rises in confrontation and burns in desire. Now consecrated under God, this animal power fuels your journey through Primordeia.`}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Garment Collection */}
        <GarmentCollection />

        {/* Tier Description */}
        <motion.div
          {...fadeInUpDelayed(0.2)}
          className="rounded-2xl p-6 mb-6"
          style={{ backgroundColor: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(12px)', boxShadow: '0 8px 32px rgba(79,55,139,0.1)' }}
          data-testid="tier-description"
        >
          <h2 className="text-lg font-bold mb-3" style={{ fontFamily: "'Fredoka', sans-serif", color: '#4F378B' }}>
            The Teaching
          </h2>
          <p className="text-sm leading-relaxed mb-3" style={{ color: '#5C5470', fontFamily: "'Nunito', sans-serif" }}>
            {tierData.description}
          </p>
          {tierData.fullDescription && (
            <>
              <button
                onClick={() => setShowFullDescription(!showFullDescription)}
                className="flex items-center gap-1 text-sm font-semibold mb-3"
                style={{ color: '#7B5EA7' }}
                data-testid="toggle-full-description"
              >
                {showFullDescription ? 'Read less' : 'Read the full teaching'}
                {showFullDescription ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              <AnimatePresence>
                {showFullDescription && (
                  <motion.div
                    {...expandHeight}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <p className="text-sm leading-relaxed whitespace-pre-line" style={{ color: '#5C5470', fontFamily: "'Nunito', sans-serif" }}>
                      {tierData.fullDescription}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </>
          )}
        </motion.div>

        {/* Always-visible Road Ahead — peek at the next tier */}
        {nextTierData && (
          <motion.div
            {...fadeInUpDelayed(0.25)}
            className="mb-6"
            data-testid="journey-road-ahead"
          >
            <RoadAheadCard nextTierName={nextTierData.name} variant="full" title="The road ahead" />
            <button
              onClick={() => setShowRoadAheadModal(true)}
              className="mt-2 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-xs font-bold transition-transform hover:scale-[1.01]"
              style={{
                background: "linear-gradient(135deg, #4F378B 0%, #7B5EA7 100%)",
                color: "#FFF",
                fontFamily: "'Fredoka', sans-serif",
                letterSpacing: "0.06em",
                boxShadow: "0 4px 14px rgba(79,55,139,0.25)"
              }}
              data-testid="preview-road-ahead-btn"
            >
              <Compass size={14} />
              Preview the Road Ahead
            </button>
          </motion.div>
        )}

        {/* Meditation */}
        {tierData.meditation && (
          <motion.div
            {...fadeInUpDelayed(0.3)}
            className="rounded-2xl p-6 mb-6"
            style={{ backgroundColor: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(12px)', boxShadow: '0 8px 32px rgba(79,55,139,0.1)' }}
            data-testid="meditation-section"
          >
            <h2 className="text-lg font-bold mb-3" style={{ fontFamily: "'Fredoka', sans-serif", color: '#4F378B' }}>Meditation</h2>
            <p className="text-sm leading-relaxed mb-4" style={{ color: '#5C5470', fontFamily: "'Nunito', sans-serif" }}>
              {tierData.meditation}
            </p>
            {meditationComplete ? (
              <div className="flex items-center gap-2 p-3 rounded-xl" style={{ backgroundColor: '#E8F5E8' }}>
                <Check size={20} style={{ color: '#4CAF50' }} />
                <span className="text-sm font-semibold" style={{ color: '#2E7D32' }}>Meditation complete</span>
              </div>
            ) : meditating ? (
              <div className="text-center">
                <div className="relative w-32 h-32 mx-auto mb-4">
                  <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                    <circle cx="50" cy="50" r="45" fill="none" stroke="#E8E0F0" strokeWidth="6" />
                    <motion.circle cx="50" cy="50" r="45" fill="none" stroke="#7B5EA7" strokeWidth="6"
                      strokeLinecap="round" strokeDasharray={283}
                      strokeDashoffset={283 - (283 * meditationTimer / MEDITATION_DURATION)} />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-2xl font-bold" style={{ color: '#4F378B', fontFamily: "'Fredoka', sans-serif" }}>
                      {formatTime(MEDITATION_DURATION - meditationTimer)}
                    </span>
                  </div>
                </div>
                <p className="text-sm" style={{ color: '#8B7DA8' }}>Breathe. Give the Divine programmer access...</p>
              </div>
            ) : (
              <motion.button
                {...tapHover}
                onClick={startMeditation}
                className="w-full py-4 rounded-xl text-white font-semibold"
                style={{ background: 'linear-gradient(135deg, #4F378B 0%, #7B5EA7 100%)', fontFamily: "'Nunito', sans-serif" }}
                data-testid="start-meditation-btn"
              >
                Begin 1 Minute Meditation
              </motion.button>
            )}
          </motion.div>
        )}

        {/* Audio Player */}
        {tierData.songUrl && (
          <motion.div
            {...fadeInUpDelayed(0.4)}
            className="rounded-2xl p-6 mb-6"
            style={{ backgroundColor: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(12px)', boxShadow: '0 8px 32px rgba(79,55,139,0.1)' }}
            data-testid="audio-player-section"
          >
            <h2 className="text-lg font-bold mb-1" style={{ fontFamily: "'Fredoka', sans-serif", color: '#4F378B' }}>
              {tierData.songTitle || 'Sacred Audio'}
            </h2>
            <p className="text-xs mb-4" style={{ color: '#8B7DA8' }}>Listen to complete the experience</p>
            <audio ref={audioRef} src={tierData.songUrl} onTimeUpdate={handleTimeUpdate} onEnded={handleAudioEnd} onLoadedMetadata={handleTimeUpdate} preload="metadata" />
            <div className="flex items-center gap-4">
              <motion.button {...tapHoverIcon} onClick={toggleAudio}
                className="w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: 'linear-gradient(135deg, #D4A574 0%, #C4956A 100%)' }}
                data-testid="audio-play-btn"
              >
                {isPlaying ? <Pause size={24} style={{ color: '#FFF' }} /> : <Play size={24} style={{ color: '#FFF', marginLeft: 2 }} />}
              </motion.button>
              <div className="flex-1">
                <div className="w-full h-2 rounded-full cursor-pointer" style={{ backgroundColor: '#E8E0F0' }}
                  onClick={(e) => {
                    if (!audioRef.current || !audioDuration) return;
                    const rect = e.currentTarget.getBoundingClientRect();
                    audioRef.current.currentTime = ((e.clientX - rect.left) / rect.width) * audioDuration;
                  }}
                >
                  <div className="h-full rounded-full transition-all" style={{
                    width: audioDuration ? `${(audioProgress / audioDuration) * 100}%` : '0%',
                    background: 'linear-gradient(90deg, #D4A574, #4F378B)'
                  }} />
                </div>
                <div className="flex justify-between mt-1">
                  <span className="text-xs" style={{ color: '#8B7DA8' }}>{formatTime(audioProgress)}</span>
                  <span className="text-xs" style={{ color: '#8B7DA8' }}>{audioDuration ? formatTime(audioDuration) : '--:--'}</span>
                </div>
              </div>
              <Volume2 size={18} style={{ color: '#8B7DA8' }} />
            </div>
          </motion.div>
        )}

        {/* Journey Timeline */}
        <motion.div
          {...fadeInUpDelayed(0.5)}
          className="rounded-2xl p-6 mb-8"
          style={{
            background: 'linear-gradient(180deg, #0D0A1A 0%, #1A1030 40%, #0D1A10 100%)',
            boxShadow: '0 12px 48px rgba(13, 10, 26, 0.4)'
          }}
          data-testid="tier-map"
        >
          <h2 className="text-lg font-bold mb-3" style={{ fontFamily: "'Fredoka', sans-serif", color: '#D4A574' }}>
            The Path to Primordeia
          </h2>
          {/* Tier-band legend — Botaneia (1-10) → Anamalia (11-27) → Noctyra (28+) */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-4 text-[10px] tracking-widest uppercase"
            style={{ fontFamily: "'Cinzel', serif" }}
            data-testid="tier-band-legend">
            <span className="flex items-center gap-1.5"
              style={{ color: currentTier < 10 ? '#7BC97B' : '#5C5470' }}>
              <span className="inline-block w-2 h-2 rounded-full"
                style={{ background: currentTier < 10 ? 'linear-gradient(135deg,#4CAF50,#7BC97B)' : 'rgba(139,125,168,0.3)' }} />
              Tiers 1&ndash;10 · Botaneia
            </span>
            <span className="flex items-center gap-1.5"
              style={{ color: currentTier >= 10 && currentTier < 27 ? '#D4A574' : '#5C5470' }}>
              <span className="inline-block w-2 h-2 rounded-full"
                style={{ background: currentTier >= 10 && currentTier < 27 ? 'linear-gradient(135deg,#7B5EA7,#D4A574)' : 'rgba(139,125,168,0.3)' }} />
              Tiers 11&ndash;27 · Anamalia
            </span>
            <span className="flex items-center gap-1.5"
              style={{ color: currentTier >= 27 ? '#B5A8FF' : '#5C5470' }}>
              <span className="inline-block w-2 h-2 rounded-full"
                style={{ background: currentTier >= 27 ? 'linear-gradient(135deg,#3A2E80,#B5A8FF)' : 'rgba(139,125,168,0.3)' }} />
              Tiers 28&ndash;32 · Noctyra
            </span>
          </div>
          <div ref={tierMapScrollRef} className="relative overflow-x-auto pb-4" style={{ scrollbarWidth: 'thin' }} data-testid="tier-map-scroll">
            <div className="flex items-start gap-0" style={{ minWidth: `${allTiers.length * 90}px` }}>
              {allTiers.map((tier, idx) => {
                const isCompleted = idx < currentTier;
                const isCurrent = idx === currentTier;
                // Tier-band start markers — render a small banner above the FIRST tier in each band
                let bandStart = null;
                if (idx === 0) bandStart = 'Botaneia';
                else if (idx === 10) bandStart = 'Anamalia';
                else if (idx === 27) bandStart = 'Noctyra';

                const lineBackground = isCompleted
                  ? 'linear-gradient(90deg, #D4A574, #4CAF50)'
                  : isCurrent
                  ? 'linear-gradient(90deg, #4CAF50, #4F378B)'
                  : 'rgba(139, 125, 168, 0.15)';

                const nodeBackground = isCompleted
                  ? 'linear-gradient(135deg, #2E7D32, #4CAF50)'
                  : isCurrent
                  ? 'linear-gradient(135deg, #4F378B, #7B5EA7)'
                  : 'rgba(139, 125, 168, 0.15)';

                const nodeShadow = isCompleted
                  ? '0 0 12px rgba(76, 175, 80, 0.4)'
                  : isCurrent
                  ? '0 0 16px rgba(79, 55, 139, 0.5)'
                  : 'none';

                const nameColor = isCompleted ? '#4CAF50' : isCurrent ? '#D4A574' : '#5C5470';
                const userMarker = userMapMarker;

                return (
                  <div key={tier.id}
                    ref={isCurrent ? currentTierNodeRef : null}
                    className="flex flex-col items-center relative" style={{ minWidth: 90 }}>
                    {/* Tier-band banner above the first node of each band */}
                    {bandStart && (
                      <div className="absolute -top-2 left-0 text-[9px] font-bold uppercase tracking-[0.18em] whitespace-nowrap px-2 py-0.5 rounded-full"
                        style={{
                          background: bandStart === 'Botaneia'
                            ? 'linear-gradient(90deg, rgba(76,175,80,0.6), rgba(76,175,80,0.0))'
                            : bandStart === 'Anamalia'
                              ? 'linear-gradient(90deg, rgba(212,165,116,0.6), rgba(212,165,116,0.0))'
                              : 'linear-gradient(90deg, rgba(123,94,167,0.7), rgba(123,94,167,0.0))',
                          color: '#FFF',
                          fontFamily: "'Cinzel', serif",
                          transform: 'translateY(-110%)'
                        }}
                        data-testid={`tier-band-${bandStart.toLowerCase()}`}
                      >
                        {bandStart === 'Botaneia' ? '🌿 ' : bandStart === 'Anamalia' ? '🐾 ' : '✨ '}{bandStart}
                      </div>
                    )}
                    {/* Connecting line */}
                    {idx > 0 && (
                      <div className="absolute top-5 right-1/2 h-0.5"
                        style={{ width: 90, transform: 'translateX(50%)', background: lineBackground, zIndex: 0 }}
                      />
                    )}

                    {/* Node */}
                    <motion.div
                      initial={false}
                      animate={isCurrent ? { scale: [1, 1.15, 1], boxShadow: ['0 0 0px #7B5EA7', '0 0 20px #7B5EA7', '0 0 0px #7B5EA7'] } : {}}
                      transition={isCurrent ? { repeat: Infinity, duration: 2.5 } : {}}
                      className="relative z-10 w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold"
                      style={{
                        background: nodeBackground,
                        color: isCompleted || isCurrent ? '#FFF' : '#5C5470',
                        fontFamily: "'Fredoka', sans-serif",
                        boxShadow: nodeShadow,
                        border: isCurrent ? '2px solid #D4A574' : 'none'
                      }}
                    >
                      {isCompleted ? <Check size={16} /> : tier.level}
                    </motion.div>

                    {/* User marker on current tier */}
                    {isCurrent && (
                      <motion.div
                        animate={{ y: [-2, 2, -2] }}
                        transition={{ repeat: Infinity, duration: 1.5 }}
                        className="text-lg mt-1"
                        style={{ lineHeight: 1 }}
                        data-testid="user-marker"
                        title={userMarkerLabel}
                        aria-label={userMarkerLabel}
                      >
                        {userMarker}
                      </motion.div>
                    )}

                    {/* Tier name */}
                    <div className={`mt-${isCurrent ? '1' : '2'} text-center px-1`}>
                      <div className="text-[10px] font-bold leading-tight"
                        style={{ color: nameColor, fontFamily: "'Fredoka', sans-serif" }}
                      >
                        {tier.name}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          {/* Legend */}
          <div className="flex items-center gap-4 mt-4 pt-3 border-t" style={{ borderColor: 'rgba(139,125,168,0.15)' }}>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full" style={{ background: '#4CAF50', boxShadow: '0 0 6px rgba(76,175,80,0.4)' }} />
              <span className="text-[10px]" style={{ color: '#8B7DA8' }}>Completed</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full" style={{ background: '#4F378B', boxShadow: '0 0 6px rgba(79,55,139,0.4)' }} />
              <span className="text-[10px]" style={{ color: '#8B7DA8' }}>Current</span>
            </div>
            <div className="flex items-center gap-1.5" data-testid="journey-legend-user">
              <span className="text-sm">{userMapMarker}</span>
              <span className="text-[10px]" style={{ color: '#8B7DA8' }}>{userMarkerLabel}</span>
            </div>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} className="text-center pb-8">
          <p className="text-sm" style={{ color: '#8B7DA8', fontFamily: "'Nunito', sans-serif" }}>
            Click the bunny in the corner to talk with Papaya and earn blueberries
          </p>
        </motion.div>
      </div>

      {/* Floating Now-Playing mini pill — appears while the tier song plays, scroll-persistent */}
      <AnimatePresence>
        {isPlaying && tierData.songUrl && (
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.9 }}
            transition={{ type: "spring", damping: 18, stiffness: 220 }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[90] pointer-events-auto"
            data-testid="now-playing-pill"
          >
            <button
              type="button"
              onClick={() => {
                const section = document.querySelector('[data-testid="audio-player-section"]');
                if (section) section.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }}
              className="flex items-center gap-3 px-4 py-2.5 rounded-full shadow-2xl max-w-[88vw]"
              style={{
                background: "linear-gradient(135deg, rgba(79,55,139,0.95) 0%, rgba(123,94,167,0.95) 100%)",
                backdropFilter: "blur(10px)",
                color: "#FFF",
                border: "1px solid rgba(255,255,255,0.18)",
                boxShadow: "0 12px 36px rgba(79,55,139,0.45)"
              }}
              title="Tap to scroll to the player"
            >
              {/* Animated sound bars */}
              <div className="flex items-end gap-0.5 h-4 flex-shrink-0" aria-hidden="true">
                {[0, 1, 2, 3].map((i) => (
                  <motion.div
                    key={i}
                    className="w-0.5 rounded-full bg-white"
                    animate={{ height: ["25%", "85%", "40%", "70%", "25%"] }}
                    transition={{
                      repeat: Infinity,
                      duration: 1 + i * 0.15,
                      ease: "easeInOut",
                      delay: i * 0.08
                    }}
                    style={{ height: "40%" }}
                  />
                ))}
              </div>
              <span className="text-xs font-bold whitespace-nowrap" style={{ fontFamily: "'Fredoka', sans-serif", letterSpacing: "0.02em" }}>
                {"\u{1F3B5}"} Now Playing
              </span>
              <span className="text-xs truncate max-w-[40vw] opacity-90" style={{ fontFamily: "'Nunito', sans-serif" }}>
                {tierData.songTitle || 'Sacred Audio'}
              </span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (audioRef.current) { audioRef.current.pause(); }
                  setIsPlaying(false);
                }}
                className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center hover:bg-white/15 transition"
                data-testid="now-playing-pause"
                aria-label="Pause song"
              >
                <Pause size={12} style={{ color: '#FFF' }} />
              </button>
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Road Ahead Modal */}
      <AnimatePresence>
        {showRoadAheadModal && nextTierData && (
          <motion.div
            initial={fadeIn.initial}
            animate={fadeIn.animate}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[300] flex items-center justify-center px-4"
            style={{ background: "rgba(20, 14, 32, 0.7)", backdropFilter: "blur(8px)" }}
            onClick={() => setShowRoadAheadModal(false)}
            data-testid="road-ahead-modal"
          >
            <motion.div
              initial={{ scale: 0.92, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.92, opacity: 0, y: 10 }}
              transition={{ type: "spring", damping: 18, stiffness: 200 }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-w-md w-full rounded-3xl p-6"
              style={{
                background: "linear-gradient(135deg, #FFFFFF 0%, #F8F4FF 100%)",
                boxShadow: "0 24px 80px rgba(79,55,139,0.4)"
              }}
            >
              <button
                onClick={() => setShowRoadAheadModal(false)}
                className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center hover:bg-purple-50"
                style={{ color: "#8B7DA8" }}
                data-testid="close-road-ahead-modal"
                aria-label="Close"
              >
                ×
              </button>
              <div className="flex items-center gap-2 mb-4">
                <Compass size={16} style={{ color: "#4F378B" }} />
                <h3 className="text-base font-bold" style={{ color: "#4F378B", fontFamily: "'Fredoka', sans-serif" }}>
                  A peek through the trees
                </h3>
              </div>
              <RoadAheadCard nextTierName={nextTierData.name} variant="full" title="What awaits beyond this tier" />
              <p className="text-xs italic mt-4 text-center" style={{ color: "#8B7DA8", fontFamily: "'Nunito', sans-serif" }}>
                The garment is fogged because it cannot be worn until earned.
                <br />
                Continue speaking with Papaya to walk towards it.
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      </>
      )}
    </div>
  );
};

export default Journey;
